import { useMemo, useState } from 'react';
import { FitText } from '../../components/FitText.js';
import { useT, type CatalogKey } from '../../i18n/index.js';
import { SUPPORT_CHAT } from '../../lib/support.js';
import { openExternal } from '../../services/telegram.js';

type BanGroup = 'SCRIPT' | 'REVIEW' | 'MULTI' | 'FAKE' | 'FARM' | 'OTHER';

/** What the server sends with a refusal for a suspended account (server: modules/auth/ban.ts). */
interface BanDetails {
  code: string | null;
  group: BanGroup;
  reference: string;
  bannedAt: number;
}

const GROUPS: readonly BanGroup[] = ['SCRIPT', 'REVIEW', 'MULTI', 'FAKE', 'FARM', 'OTHER'];

/** Each reason the panel can give, its explanation, and the point of the Terms it breaks. */
const REASONS: Record<BanGroup, { title: CatalogKey; body: CatalogKey; point: number }> = {
  SCRIPT: { title: 'ban.reason.SCRIPT.title', body: 'ban.reason.SCRIPT.body', point: 2 },
  REVIEW: { title: 'ban.reason.REVIEW.title', body: 'ban.reason.REVIEW.body', point: 2 },
  MULTI: { title: 'ban.reason.MULTI.title', body: 'ban.reason.MULTI.body', point: 2 },
  FAKE: { title: 'ban.reason.FAKE.title', body: 'ban.reason.FAKE.body', point: 2 },
  FARM: { title: 'ban.reason.FARM.title', body: 'ban.reason.FARM.body', point: 2 },
  OTHER: { title: 'ban.reason.OTHER.title', body: 'ban.reason.OTHER.body', point: 2 },
};

/**
 * The title, painted on the gold plate, per language. None for now: every
 * language sets it live on the empty plate (ui/ban/button.webp), in the dark
 * ink of the task buttons — the painted Spanish one was gold on gold
 * (ui/ban/title-es.webp, kept for if it is ever repainted).
 */
const PAINTED_TITLE: Partial<Record<string, string>> = {};

/** The Terms as Profile shows them; the broken point is marked. */
const TERMS: readonly CatalogKey[] = ['profile.legal1', 'profile.legal2', 'profile.legal3', 'profile.legal4'];

function readBan(details: unknown): BanDetails | null {
  const ban = (details as { ban?: unknown } | null)?.ban as Partial<BanDetails> | null | undefined;
  if (!ban || typeof ban !== 'object' || typeof ban.reference !== 'string' || typeof ban.bannedAt !== 'number') return null;
  return {
    code: typeof ban.code === 'string' ? ban.code : null,
    group: GROUPS.includes(ban.group as BanGroup) ? (ban.group as BanGroup) : 'OTHER',
    reference: ban.reference,
    bannedAt: ban.bannedAt,
  };
}

/**
 * The screen a suspended account opens to, instead of the app.
 *
 * Everything on it comes from the ban the panel wrote (Usuarios → Banear): the
 * reason by its code, explained, with the point of the Terms and Conditions it
 * breaks quoted; when it started; that it is permanent (only an admin lifts a
 * ban); and the reference support asks for. The Terms can be read in full
 * from here, since a suspended account cannot reach Profile. Support opens
 * straight in Telegram.
 *
 * The art is Igu's, from ChatGPT (ui/ban/): the sealed mine, the gold title
 * plate hung on its sign, the stone panel the facts sit on — cut in nine so it
 * grows with each language — and the gold plate of the support button.
 */
export function BannedScreen({ details }: { details: unknown }) {
  const { t, locale } = useT();
  const ban: BanDetails | null = useMemo(() => readBan(details), [details]);
  const reason = REASONS[ban?.group ?? 'OTHER'];
  const [showTerms, setShowTerms] = useState(false);
  const [copied, setCopied] = useState(false);

  const formatDate = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  }, [locale]);

  const copyReference = async () => {
    if (!ban) return;
    try {
      await navigator.clipboard.writeText(ban.reference);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Nothing to copy into: the reference stays on screen to read out.
    }
  };

  const painted = PAINTED_TITLE[locale];
  const title = t('error.USER_SUSPENDED.title');

  return (
    <div className="ban" role="alert">
      <div className="ban__frame">
        <div className="ban__stage">
          <h1 className="visually-hidden">{title}</h1>
          {painted ? (
            <img className="ban__plate" src={painted} alt="" aria-hidden draggable={false} />
          ) : (
            <div className="ban__plate ban__plate--live" aria-hidden>
              <FitText className="ban__plate-text">{title}</FitText>
            </div>
          )}
        </div>

        <section className="ban__panel">
          <p className="ban__lead">{t(reason.title)}</p>
          <p className="ban__why">{t(reason.body)}</p>

          <figure className="ban__rule">
            <figcaption className="ban__rule-label">{t('ban.rule', { point: reason.point })}</figcaption>
            <blockquote className="ban__rule-text">{t(TERMS[reason.point - 1] ?? 'profile.legal2')}</blockquote>
          </figure>

          <dl className="ban__facts">
            {ban?.code ? (
              <div className="ban__fact">
                <dt>{t('ban.code')}</dt>
                <dd className="ban__mono">{ban.code}</dd>
              </div>
            ) : null}
            {ban ? (
              <div className="ban__fact">
                <dt>{t('ban.date')}</dt>
                <dd>{formatDate.format(ban.bannedAt)}</dd>
              </div>
            ) : null}
            <div className="ban__fact">
              <dt>{t('ban.duration')}</dt>
              <dd>{t('ban.permanent')}</dd>
            </div>
            {ban ? (
              <div className="ban__fact">
                <dt>{t('ban.reference')}</dt>
                <dd>
                  <button type="button" className="ban__ref" onClick={() => void copyReference()}>
                    <span className="ban__mono">{ban.reference}</span>
                    <span className="ban__ref-copy">{copied ? t('common.copied') : t('common.copy')}</span>
                  </button>
                </dd>
              </div>
            ) : null}
          </dl>
        </section>

        <div className="ban__actions">
          <button type="button" className="ban__primary" onClick={() => openExternal(SUPPORT_CHAT)}>
            <FitText as="span" className="ban__primary-text">
              {t('ban.support')}
            </FitText>
          </button>
          <p className="ban__hint">{t('ban.supportHint')}</p>
          <button type="button" className="ban__terms-toggle" aria-expanded={showTerms} onClick={() => setShowTerms(true)}>
            {t('ban.terms')}
          </button>
        </div>

        {showTerms ? (
          <div className="ban__sheet" role="dialog" aria-modal="true" aria-label={t('ban.terms')} onClick={() => setShowTerms(false)}>
            <section className="ban__sheet-card" onClick={(event) => event.stopPropagation()}>
              <h2 className="ban__sheet-title">{t('ban.terms')}</h2>
              <ol className="ban__sheet-list">
                {TERMS.map((key, index) => (
                  <li key={key} className={index + 1 === reason.point ? 'is-broken' : undefined}>
                    {t(key)}
                  </li>
                ))}
              </ol>
              <button type="button" className="ban__sheet-close" onClick={() => setShowTerms(false)}>
                {t('ban.termsHide')}
              </button>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
